import { CompatiblePluginEventType } from "roosterjs";
import { Key } from "@ms/uno-constants/lib/local/KeyCode";
import { AtMentionPlaceholderClassName } from "@ms/uno-constants/lib/local/ChatConstants";
import { createMentionRenderedElement } from "../../../../utilities/ChatAtMentionsUtilities";
import debounce from "lodash/debounce";
const filterTextDebounceDelay = 200; // Delay for debouncing the filter text updates
/**
 * Rooster plugin that handles @ mentions in the chat compose section.
 */ export class AtMentionRoosterPlugin {
    getName() {
        return "AtMentionRoosterPlugin";
    }
    initialize(editor) {
        this.editorRef = editor;
    }
    dispose() {
        this.exitAtMentionMode();
        this.editorRef = null;
    }
    onPluginEvent(event) {
        if (!this.editorRef) {
            return;
        }
        const { isAtMentionPickerOpen, showAtMentionPicker, setAtMentionFilterText, highlightPreviousUser, highlightNextUser, insertHighlightedUser } = this.atMentionPickerControlsProvider();
        if (!isAtMentionPickerOpen) {
            if (event.eventType === CompatiblePluginEventType.Input) {
                if (event.rawEvent.data === "@" && event.rawEvent.inputType === "insertText") {
                    const range = this.editorRef.getSelectionRange();
                    if (range) {
                        const startContainer = range.startContainer;
                        const startOffset = range.startOffset;
                        // Only activate if @ character is at the beginning or after a space
                        const isAtBeginning = startOffset === 1;
                        const charBeforeAt = startOffset > 1 ? startContainer.textContent?.charAt(startOffset - 2) : null;
                        // Rooster alternates between space and non-breaking space
                        const isAfterSpace = charBeforeAt?.trim() === "";
                        if (!isAtBeginning && !isAfterSpace) {
                            return;
                        }
                        // Remove @ character that just triggered the event
                        if (startContainer.nodeType === Node.TEXT_NODE && startOffset > 0) {
                            const textNode = startContainer;
                            textNode.deleteData(startOffset - 1, 1);
                        }
                    }
                    // Insert at mention placeholder element
                    const element = this.createAtMentionPlaceholderElement();
                    this.editorRef.insertNode(element);
                    setAtMentionFilterText("");
                    showAtMentionPicker(element);
                }
            }
        } else {
            // Handle all inputs (includes backspace) while in "at mention" mode
            if (event.eventType === CompatiblePluginEventType.Input) {
                // Query @ mention element
                const queryResults = this.editorRef.queryElements(`.${AtMentionPlaceholderClassName}`);
                const atMentionElement = queryResults.length > 0 ? queryResults[0] : null;
                if (!atMentionElement) {
                    // If user backspaces all content in the placeholder element, the element gets removed
                    this.exitAtMentionMode();
                } else {
                    // Get the text after the @ mention placeholder
                    const mentionText = atMentionElement.textContent || "";
                    const mentionTextWithNoAt = mentionText.startsWith("@") ? mentionText.substring(1) : mentionText;
                    // Update filter text for at mention picker (debounced)
                    this.debouncedSetAtMentionFilterText(mentionTextWithNoAt);
                }
            } else if (event.eventType === CompatiblePluginEventType.BeforeSetContent) {
                // Always exit "at mention" mode on set content
                // This happens when clearing text content on send
                this.exitAtMentionMode();
            } else if (event.eventType === CompatiblePluginEventType.KeyDown) {
                if (event.rawEvent.key === Key.UP_ARROW) {
                    highlightPreviousUser();
                    event.rawEvent.preventDefault();
                } else if (event.rawEvent.key === Key.DOWN_ARROW) {
                    highlightNextUser();
                    event.rawEvent.preventDefault();
                } else if ([
                    Key.ENTER,
                    Key.TAB
                ].some((key)=>key === event.rawEvent.key)) {
                    insertHighlightedUser();
                    event.rawEvent.preventDefault();
                    event.rawEvent.stopImmediatePropagation();
                } else if ([
                    Key.ESCAPE,
                    Key.LEFT_ARROW,
                    Key.RIGHT_ARROW
                ].some((key)=>key === event.rawEvent.key)) {
                    // Exit at mention mode and hide the picker
                    this.exitAtMentionMode();
                    event.rawEvent.preventDefault();
                    event.rawEvent.stopPropagation();
                }
            }
        }
    // TODO: On blur, exit "at mention" mode and hide the picker
    }
    /**
     * Insert the user mention into the editor by replacing the placeholder element.
     * @param userId - The ID of the user to mention
     * @param displayName - The display name of the user to mention
     * @param itemType - The type of the item being mentioned
     */ insertAtMentionUser(userId, displayName, itemType) {
        const { isAtMentionPickerOpen } = this.atMentionPickerControlsProvider();
        if (!this.editorRef || !isAtMentionPickerOpen) {
            return;
        }
        // Query @ mention element
        const queryResults = this.editorRef.queryElements(`.${AtMentionPlaceholderClassName}`);
        const atMentionElement = queryResults.length > 0 ? queryResults[0] : null;
        if (atMentionElement) {
            const mentionUserElement = createMentionRenderedElement(userId, displayName, itemType, false);
            atMentionElement.replaceWith(mentionUserElement);
            // Exit at mention mode
            this.exitAtMentionMode();
            // Focus editor and set cursor after the at mention element
            this.editorRef.focus();
            const range = this.editorRef.getDocument().createRange();
            range.setStartAfter(mentionUserElement);
            this.editorRef.select(range);
        }
    }
    /**
     * Creates a placeholder element for @ mention.
     * This placeholder lets us reliably extract the filter text and replace with the actual user mention later.
     */ createAtMentionPlaceholderElement() {
        const element = document.createElement("span");
        element.textContent = "@";
        element.className = AtMentionPlaceholderClassName;
        return element;
    }
    /**
     * Exits the at mention mode and hides the at mention picker.
     */ exitAtMentionMode() {
        const { hideAtMentionPicker } = this.atMentionPickerControlsProvider();
        hideAtMentionPicker();
        // Replace any existing at mention placeholder elements with plain text
        const queryResults = this.editorRef?.queryElements(".at-mention-placeholder");
        if (queryResults && queryResults.length > 0) {
            queryResults.forEach((element)=>{
                if (element.parentNode) {
                    const textNode = document.createTextNode(element.textContent || "");
                    element.parentNode.replaceChild(textNode, element);
                }
            });
        }
    // TODO: Reset focus to correct location after replacing elements
    }
    constructor(atMentionPickerControlsProvider){
        this.atMentionPickerControlsProvider = atMentionPickerControlsProvider;
        this.editorRef = null;
        /**
     * Debounced function to set the filter text for the at mention picker.
     * This is used to avoid excessive updates while typing.
     */ this.debouncedSetAtMentionFilterText = debounce((text)=>{
            const { setAtMentionFilterText } = this.atMentionPickerControlsProvider();
            setAtMentionFilterText(text);
        }, filterTextDebounceDelay);
    }
}
